import type { LayerFilter, MapboxLayer } from '@/components/mapbox/types'
import { useErrorReporter } from '@/composables/useErrorReporter'
import {
	nextTick, ref, watch
} from 'vue'
import type { MapboxFeatureConfig } from './types'
import { DEFAULT_FEATURE_CONFIG } from './types'
import { useMapboxState } from './useMapboxState'

type FilterBundle = {
	next: LayerFilter[]
	prev: LayerFilter[]
}

export function useMapboxLayers(
	mapId: string,
	layers: MapboxLayer[] = [],
	config: MapboxFeatureConfig = DEFAULT_FEATURE_CONFIG
) {
	const { mapState } = useMapboxState(mapId)
	const { bugsnagReport } = useErrorReporter()

	// Layer visibility state tracking
	const layerVisibility = ref<Record<string, boolean>>({})

	// Queue to hold filters that arrive before the map is ready
	const pendingFilters = ref<FilterBundle | null>(null)

	const getFilterBundle = (): FilterBundle | null => {
		if (pendingFilters.value) {
			return {
				next: pendingFilters.value.next ?? [],
				prev: pendingFilters.value.prev ?? []
			}
		}

		const currentFilters = mapState.value.layerFilters ?? []
		if ((currentFilters?.length || 0) === 0) {
			return null
		}

		return {
			next: currentFilters,
			prev: []
		}
	}

	const hasQueuedFilters = () => {
		const bundle = getFilterBundle()
		if (!bundle) {
			return false
		}
		return (bundle.next?.length || 0) > 0 || (bundle.prev?.length || 0) > 0
	}

	// Initialize layer visibility state
	const initLayerVisibility = () => {
		// Initialize visibility based on provided layers
		layers.forEach(layer => {
			// Safe check for layout and visibility property
			const isVisible = layer.layer.layout && 'visibility' in layer.layer.layout
				? layer.layer.layout.visibility === 'visible'
				: false
			layerVisibility.value[layer.id] = isVisible
		})
	}

	// Add all layers to the map
	const addLayers = () => {
		if (!mapState.value.mapInstance || !mapState.value.isStyleLoaded) {
			bugsnagReport({
				error: new Error('Map not ready for adding layers'),
				showToast: false
			})
			return
		}

		const map = mapState.value.mapInstance

		// First add all sources
		layers.forEach(layer => {
			const sourceId = layer.layer.source
			if (typeof sourceId === 'string' && !map.getSource(sourceId)) {
				// Find matching source in the sources prop of Map.vue
				const sourceData = map.__mbxSources ? map.__mbxSources[sourceId] : null
				if (sourceData) {
					try {
						map.addSource(sourceId, sourceData)
					} catch (error) {
						if (!(error as Error)?.message?.includes('Style is not done loading')) {
							bugsnagReport({
								error: error as Error || new Error(`Failed to add source: ${sourceId}`),
								showToast: false
							})
						}
					}
				}
			}
		})

		// Then add all layers
		layers.forEach(layer => {
			try {
				if (!map.getLayer(layer.id)) {
					map.addLayer(layer.layer)

					// Safe check for layout and visibility property
					const isVisible = layer.layer.layout && 'visibility' in layer.layer.layout
						? layer.layer.layout.visibility === 'visible'
						: false

					const visibility = isVisible ? 'visible' : 'none'
					map.setLayoutProperty(layer.id, 'visibility', visibility)

					// Update layerVisibility state
					layerVisibility.value[layer.id] = isVisible
				}
			} catch (error) {
				if (!(error as Error)?.message?.includes('Style is not done loading')) {
					bugsnagReport({
						error: error as Error ?? new Error(`Failed to add layer: ${layer.id}`),
						showToast: false
					})
				}
			}
		})

		// Apply any pre-existing filters that were set before the map was ready
		// We need to wait for the next tick because Mapbox needs time to process the layers
		nextTick(() => {
			const existingFilters = mapState.value.layerFilters
			if (!existingFilters || existingFilters.length === 0) {
				return
			}

			// Use requestAnimationFrame to ensure the browser has rendered the layers
			requestAnimationFrame(() => {
				const map = mapState.value.mapInstance
				if (!map || !map.isStyleLoaded()) {
					applyFiltersWithRetry()
					return
				}

				// Check if all required layers exist
				const requiredLayers = [ ...new Set(existingFilters.map(f => f.layerId)) ]
				const missingLayers = requiredLayers.filter(layerId => !map.getLayer(layerId))

				if (missingLayers.length > 0) {
					// Try again after a delay
					setTimeout(() => {
						updateLayerFilters(existingFilters, [])
					}, 500)
					return
				}

				// Force apply the filters by clearing and re-applying
				// This ensures the filters are definitely applied
				existingFilters.forEach(filter => {
					try {
						const layer = map.getLayer(filter.layerId)
						if (layer) {
							map.setFilter(filter.layerId, filter.expression)
						}
					} catch (error) {
						bugsnagReport({
							error: error as Error ?? new Error(`Failed to apply filter to ${filter.layerId}`),
							showToast: false
						})
					}
				})

				if (pendingFilters.value) {
					const { next = [], prev = [] } = pendingFilters.value
					if (runFilterUpdate(next, prev)) {
						pendingFilters.value = null
					}
				}
			})
		})
	}

	// Toggle layer visibility
	const toggleLayerVisibility = (layerId: string, visible: boolean) => {
		if (!mapState.value.mapInstance) {
			return
		}

		const map = mapState.value.mapInstance
		layerVisibility.value[layerId] = visible

		try {
			if (map.getLayer(layerId)) {
				map.setLayoutProperty(
					layerId,
					'visibility',
					visible ? 'visible' : 'none'
				)
			}

			// Find the layer to get sibling layers (if any)
			const layer = layers.find(l => l.id === layerId)
			if (layer?.siblingLayerIds?.length) {
				// Also toggle sibling layers
				layer.siblingLayerIds.forEach(siblingId => {
					layerVisibility.value[siblingId] = visible
					if (map.getLayer(siblingId)) {
						map.setLayoutProperty(
							siblingId,
							'visibility',
							visible ? 'visible' : 'none'
						)
					}
				})
			}
		} catch (error) {
			bugsnagReport({
				error: error as Error ?? new Error(`Failed to toggle layer: ${layerId}`),
				showToast: false
			})
		}
	}

	// Update GeoJSON data for a source
	const updateSourceData = (sourceId: string, data: any) => {
		if (!mapState.value.mapInstance) {
			return
		}

		const map = mapState.value.mapInstance
		try {
			const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource
			if (source && source.setData) {
				source.setData(data)
			}
		} catch (error) {
			bugsnagReport({
				error: error as Error ?? new Error(`Failed to update source: ${sourceId}`),
				showToast: false
			})
		}
	}

	// Core routine to apply filters directly to the map instance when available
	const runFilterUpdate = (
		nextFilters: LayerFilter[] = [],
		prevFilters: LayerFilter[] = []
	): boolean => {
		const map = mapState.value.mapInstance
		if (!map || !map.isStyleLoaded()) {
			return false
		}

		// Get all unique layer IDs from both old and new filters so we can add/update/remove
		const allLayerIds = new Set<string>()
		prevFilters?.forEach(f => allLayerIds.add(f.layerId))
		nextFilters?.forEach(f => allLayerIds.add(f.layerId))

		const missingLayerIds = [
			...allLayerIds
		].filter(layerId => !map.getLayer(layerId))

		if (missingLayerIds.length > 0) {
			return false
		}

		// Apply updates for each affected layer
		allLayerIds.forEach(layerId => {
			const layer = map.getLayer(layerId)
			if (!layer) {
				return
			}

			let layerFilters = nextFilters?.filter(f => f.layerId === layerId) || []

			// Use injected config for priority sorting of special filters
			const tableDataMarkerId = config.specialFilterIds?.tableDataMarkerId
			if (tableDataMarkerId) {
				layerFilters = layerFilters.sort((a, b) => {
					const aIsMarkerFilter = a.filterId === tableDataMarkerId
					const bIsMarkerFilter = b.filterId === tableDataMarkerId
					if (aIsMarkerFilter && !bIsMarkerFilter) return -1
					if (!aIsMarkerFilter && bIsMarkerFilter) return 1
					return 0
				})
			}

			try {
				if (layerFilters.length === 0) {
					map.setFilter(layerId, null)
				} else if (layerFilters.length === 1) {
					map.setFilter(layerId, layerFilters[0].expression)
				} else {
					const combinedExpression = [
						'all',
						...layerFilters.map(f => f.expression)
					]
					map.setFilter(layerId, combinedExpression)
				}
			} catch (error) {
				bugsnagReport({
					error: error as Error ?? new Error(`Failed to apply filter to layer: ${layerId}`),
					showToast: false
				})
			}
		})

		return true
	}

	const applyFiltersWithRetry = (retryCount = 0) => {
		const bundle = getFilterBundle()
		if (!bundle || (!bundle.next?.length && !bundle.prev?.length)) {
			return
		}

		const map = mapState.value.mapInstance
		if (!map || !map.isStyleLoaded()) {
			if (retryCount < 10) {
				setTimeout(() => applyFiltersWithRetry(retryCount + 1), 100)
			} else {
				bugsnagReport({
					error: new Error('Failed to apply filters after retries (map not ready)'),
					showToast: false
				})
			}
			return
		}

		const requiredLayers = [
			...new Set([
				...(bundle.next?.map(f => f.layerId) || []),
				...(bundle.prev?.map(f => f.layerId) || [])
			])
		]

		const missingLayers = requiredLayers.filter(layerId => !map.getLayer(layerId))
		if (missingLayers.length > 0) {
			if (retryCount < 10) {
				setTimeout(() => applyFiltersWithRetry(retryCount + 1), 100)
			} else {
				bugsnagReport({
					error: new Error(`Failed to apply filters after retries (missing layers: ${missingLayers.join(', ')})`),
					showToast: false
				})
			}
			return
		}

		const applied = runFilterUpdate(bundle.next || [], bundle.prev || [])
		if (!applied) {
			if (retryCount < 10) {
				setTimeout(() => applyFiltersWithRetry(retryCount + 1), 100)
			} else {
				bugsnagReport({
					error: new Error('Failed to apply filters after retries (runFilterUpdate unavailable)'),
					showToast: false
				})
			}
			return
		}

		pendingFilters.value = null
	}

	// Apply layer filters to the map or queue them until the map is ready
	const updateLayerFilters = (
		newFilters: LayerFilter[] | undefined,
		oldFilters: LayerFilter[] | undefined
	) => {
		const nextFilters = newFilters ?? []
		const prevFilters = oldFilters ?? []

		const applied = runFilterUpdate(nextFilters, prevFilters)
		if (!applied) {
			pendingFilters.value = {
				next: nextFilters,
				prev: prevFilters
			}
			if (mapState.value.isStyleLoaded) {
				applyFiltersWithRetry()
			}
			return
		}

		// Successfully applied, clear any queued filters
		pendingFilters.value = null
	}

	// Watch for filter changes
	watch(
		() => mapState.value.layerFilters,
		(newFilters, oldFilters) => {
			updateLayerFilters(newFilters, oldFilters)
		},
		{
			deep: true,
			immediate: true
		}
	)

	// Watch for map becoming ready - apply any queued filters with retry logic
	watch(
		() => mapState.value.isStyleLoaded,
		(isLoaded, wasLoaded) => {
			if (isLoaded && !wasLoaded && hasQueuedFilters()) {
				applyFiltersWithRetry()
			}
		}
	)

	// Watch for layer visibility changes
	watch(layerVisibility, newVisibility => {
		Object.entries(newVisibility).forEach(([ layerId, isVisible ]) => {
			toggleLayerVisibility(layerId, isVisible)
		})
	}, { deep: true })

	return {
		layerVisibility,
		initLayerVisibility,
		addLayers,
		toggleLayerVisibility,
		updateSourceData,
		updateLayerFilters
	}
}
